import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo } from "react";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { ChartIndexCompare } from "@app/components/ChartIndexCompare";
import { useAssetClassAnalysisIndexCompare } from "./useAssetClassAnalysisIndexCompare";
import { TableIndexCompare } from "@app/components/TableIndexCompare";
import { InternalUse } from "../assetAllocation/editPortfolioGroup/InternalUse";
import { formatDate, openInNewTab } from "@app/utils";
import { CarDownloadButton } from "@app/components/Buttons";
import { assetClassAnalysisPages } from "@app/const/route.const";

export const AssetClassAnalysisIndexCompare = memo(() => {
  const data = useAssetClassAnalysisIndexCompare({
    storageKey: "AssetClassAnalysisIndexCompare",
  });

  const pageContent = usePageContent();

  const handleShowPdf = () => {
    if (
      data.chartDataSelector.data1Id ||
      data.chartDataSelector.data2Id ||
      data.chartDataSelector.data3Id
    ) {
      openInNewTab(
        assetClassAnalysisPages.getPdfIndexCompareReportUrl({
          assetClass1Id: data.chartDataSelector.data1Id ?? "",
          assetClass2Id: data.chartDataSelector.data2Id ?? "",
          assetClass3Id: data.chartDataSelector.data3Id ?? "",
        }),
      );
    }
  };

  return (
    <TablePlaceHolder
      isLoading={data.isLoading || pageContent.isLoading}
      rows={8}
    >
      <CarDownloadButton
        onClick={handleShowPdf}
        sx={{ mr: -1.5, alignSelf: "flex-end" }}
      >
        Download PDF
      </CarDownloadButton>
      <CarPageContent
        sx={{ mt: 1 }}
        isWhiteContext
        content={pageContent.getContent(
          CarPageContentCode.ASSET_CLASS_MONTHLY_REPORT_INDEX_COMPARE_REPORT,
        )}
      />

      <Box
        sx={{
          mt: 5,
          mb: 3,
          display: "flex",
          alignItems: "flex-end",
          gap: 5,
        }}
      >
        <CarAssetClassSelector
          label="Asset Class 1"
          sx={{
            width: 250,
            ".MuiInputBase-root": {
              backgroundColor: "white",
            },
          }}
          items={data.items}
          valueField="level4Id"
          value={data.chartDataSelector.data1Id}
          onChange={data.chartDataSelector.onData1IdChange}
        />
        <CarAssetClassSelector
          label="Asset Class 2"
          sx={{
            width: 250,
            ".MuiInputBase-root": {
              backgroundColor: "white",
            },
          }}
          items={data.items}
          valueField="level4Id"
          value={data.chartDataSelector.data2Id}
          onChange={data.chartDataSelector.onData2IdChange}
        />
        <CarAssetClassSelector
          label="Asset Class 3"
          sx={{
            width: 250,
            ".MuiInputBase-root": {
              backgroundColor: "white",
            },
          }}
          items={data.items}
          valueField="level4Id"
          value={data.chartDataSelector.data3Id}
          onChange={data.chartDataSelector.onData3IdChange}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          borderStyle: "solid",
          borderWidth: "1px",
          borderColor: "gray3",
          flex: "auto",
          p: 6.5,
        }}
      >
        {(data.item1 || data.item2 || data.item3) && (
          <>
            <Box
              sx={{
                mt: -2,
                ml: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
              <InternalUse />
              <Typography variant="h6SRegular" sx={{ fontStyle: "italic" }}>
                Start Date {formatDate(data.startDate)}
              </Typography>
            </Box>
            <ChartIndexCompare
              sx={{ mt: 1.5 }}
              item1={data.item1}
              item2={data.item2}
              item3={data.item3}
              isWhiteContext
            />
            <TableIndexCompare
              sx={{ mt: 6 }}
              item1={data.item1}
              item2={data.item2}
              item3={data.item3}
              isWhiteContext
            />
          </>
        )}
      </Box>
    </TablePlaceHolder>
  );
});
